import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  AlertTriangle, ChevronDown, ChevronUp, FolderOpen, ExternalLink, Sparkles, ArrowRight, EyeOff, Bell, Megaphone, Check,
} from "lucide-react";

/* -------------------------------------------------------------------------- */
/*  Iter 44 — "New Changes" banner: matches AI-banner styling. Lists every    */
/*  recent activity on projects visible to Admin / Super-Admin / Content DA. */
/*  One "Ignore" per row + "Ignore all"; per-user ack list persists on the    */
/*  server so dismissals are private and survive reloads.                     */
/* -------------------------------------------------------------------------- */
const ACTION_LABELS = {
  created:         { verb: "created",        tone: "bg-purple-100 text-purple-700 border-purple-200" },
  renamed:         { verb: "renamed",        tone: "bg-slate-100 text-slate-700 border-slate-200" },
  status_changed:  { verb: "status",         tone: "bg-amber-100 text-amber-800 border-amber-200" },
  assigned:        { verb: "team",           tone: "bg-sky-100 text-sky-700 border-sky-200" },
  deleted:         { verb: "deleted",        tone: "bg-red-100 text-red-700 border-red-200" },
  pushed_from_seo: { verb: "SEO push",       tone: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  custom_message:  { verb: "announcement",   tone: "bg-indigo-100 text-indigo-700 border-indigo-200" },
};

function formatAgo(iso) {
  if (!iso) return "";
  const s = Math.max(1, Math.round((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.round(s / 60)}m ago`;
  if (s < 86400) return `${Math.round(s / 3600)}h ago`;
  return `${Math.round(s / 86400)}d ago`;
}

export function NewChangesBanner({ notifications, onIgnore, onIgnoreAll }) {
  if (!notifications || notifications.length === 0) return null;
  return (
    <div
      className="shake-animation relative overflow-hidden rounded-xl border border-purple-200 bg-gradient-to-br from-purple-50 via-fuchsia-50 to-indigo-50 p-5"
      data-testid="new-changes-banner"
    >
      <div className="absolute -right-6 -top-6 h-32 w-32 rounded-full bg-purple-200/40 blur-3xl" />
      <div className="relative">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0">
            <div className="flex items-center gap-2 text-purple-700 font-semibold">
              <Bell className="h-5 w-5" />
              New Changes
            </div>
            <div className="text-sm text-slate-600 mt-1" data-testid="new-changes-count">
              {notifications.length} recent change{notifications.length === 1 ? "" : "s"} across your projects. Tap ✓ to mark as read.
            </div>
          </div>
          <Button
            onClick={onIgnoreAll}
            className="bg-purple-600 hover:bg-purple-700 text-white shrink-0"
            data-testid="new-changes-ignore-all"
          >
            Ignore all <ArrowRight className="h-4 w-4 ml-1.5" />
          </Button>
        </div>

        <div className="mt-3 rounded-lg bg-white/70 border border-purple-100 divide-y divide-purple-100 max-h-[320px] overflow-y-auto">
          {notifications.map((n) => {
            const meta = ACTION_LABELS[n.action] || ACTION_LABELS.assigned;
            return (
              <div
                key={n.id}
                className="flex items-center gap-2 px-3 py-2 hover:bg-white/80"
                data-testid={`new-changes-row-${n.id}`}
              >
                <span className={`shrink-0 text-[10px] font-semibold uppercase tracking-wider border px-1.5 py-0.5 rounded ${meta.tone}`}>
                  {meta.verb}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="text-sm text-slate-800 truncate">
                    <span className="font-medium text-purple-900">{n.actor_name}</span>
                    {n.action === "custom_message" && n.actor_designation && (
                      <span className="ml-1.5 text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded border bg-purple-50 text-purple-700 border-purple-200 align-middle">
                        {n.actor_designation}
                      </span>
                    )}
                    {" — "}
                    {n.action === "custom_message" && n.title && (
                      <span className="font-semibold text-slate-900">{n.title}: </span>
                    )}
                    {n.description}
                    {n.action !== "deleted" && n.action !== "custom_message" && n.project_id && (
                      <Link
                        to={`/projects/${n.project_id}`}
                        className="ml-1 text-purple-700 hover:underline font-semibold"
                        data-testid={`new-changes-open-${n.id}`}
                      >
                        {n.project_name} <ExternalLink className="inline h-3 w-3 -mt-0.5" />
                      </Link>
                    )}
                    {n.action === "deleted" && (
                      <span className="ml-1 text-slate-500 italic">"{n.project_name}"</span>
                    )}
                  </div>
                  <div className="text-[11px] text-slate-500">{formatAgo(n.created_at)}</div>
                </div>
                <button
                  type="button"
                  onClick={() => onIgnore(n)}
                  className="shrink-0 h-7 w-7 rounded-full text-emerald-600 hover:text-white hover:bg-emerald-600 border border-emerald-200 hover:border-emerald-600 transition flex items-center justify-center"
                  title="Mark as read"
                  aria-label="Mark as read"
                  data-testid={`new-changes-ignore-${n.id}`}
                >
                  <Check className="h-3.5 w-3.5" />
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Iter 44c.2 — "Announcements" banner: separate from "New Changes".        */
/*  Shows custom broadcast messages posted by Founder/Admin/Content Admin/HR. */
/*  Same interaction model (Ignore + Ignore all) but a distinct indigo        */
/*  gradient + megaphone icon so it doesn't compete visually with project     */
/*  mutations.                                                                */
/* -------------------------------------------------------------------------- */
export function AnnouncementsBanner({ announcements, onIgnore, onIgnoreAll }) {
  if (!announcements || announcements.length === 0) return null;
  return (
    <div
      className="relative overflow-hidden rounded-xl border border-indigo-200 bg-gradient-to-br from-indigo-50 via-violet-50 to-fuchsia-50 p-5"
      data-testid="announcements-banner"
    >
      <div className="absolute -right-6 -top-6 h-32 w-32 rounded-full bg-indigo-200/40 blur-3xl" />
      <div className="relative">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0">
            <div className="flex items-center gap-2 text-indigo-700 font-semibold">
              <Megaphone className="h-5 w-5" />
              Announcements
            </div>
            <div className="text-sm text-slate-600 mt-1" data-testid="announcements-count">
              {announcements.length} announcement{announcements.length === 1 ? "" : "s"} for you. Tap ✓ to mark as read.
            </div>
          </div>
          <Button
            onClick={onIgnoreAll}
            className="bg-indigo-600 hover:bg-indigo-700 text-white shrink-0"
            data-testid="announcements-ignore-all"
          >
            Ignore all <ArrowRight className="h-4 w-4 ml-1.5" />
          </Button>
        </div>

        <div className="mt-3 rounded-lg bg-white/70 border border-indigo-100 divide-y divide-indigo-100 max-h-[320px] overflow-y-auto">
          {announcements.map((n) => (
            <div
              key={n.id}
              className="flex items-center gap-2 px-3 py-2 hover:bg-white/80"
              data-testid={`announcement-row-${n.id}`}
            >
              <div className="min-w-0 flex-1">
                <div className="text-sm text-slate-800 break-words">
                  <span className="font-medium text-indigo-900">{n.actor_name}</span>
                  {n.actor_designation && (
                    <span className="ml-1.5 text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded border bg-indigo-50 text-indigo-700 border-indigo-200 align-middle">
                      {n.actor_designation}
                    </span>
                  )}
                  {n.audience_label && (
                    <span
                      className="ml-1.5 text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded border bg-slate-100 text-slate-600 border-slate-200 align-middle"
                      data-testid={`announcement-audience-${n.id}`}
                    >
                      {n.audience_label}
                    </span>
                  )}
                  {" — "}
                  {n.title && <span className="font-semibold text-slate-900">{n.title}: </span>}
                  {n.description}
                </div>
                <div className="text-[11px] text-slate-500">{formatAgo(n.created_at)}</div>
              </div>
              <button
                type="button"
                onClick={() => onIgnore(n)}
                className="shrink-0 h-7 w-7 rounded-full text-emerald-600 hover:text-white hover:bg-emerald-600 border border-emerald-200 hover:border-emerald-600 transition flex items-center justify-center"
                title="Mark as read"
                aria-label="Mark as read"
                data-testid={`announcement-ignore-${n.id}`}
              >
                <Check className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Reusable shaking-red banner shell + generic accordion row layout used by  */
/*  Unassigned / Missing-Writer / Missing-Publisher.                          */
/* -------------------------------------------------------------------------- */
function ShakeBanner({ open, title, subtitle, testId, toggleTestId, onToggle, children }) {
  return (
    <div
      className={`shake-alert relative rounded-lg border border-red-300 bg-red-50 px-4 py-3 flex items-center gap-3 ${!open ? "shake-animation" : ""}`}
      data-testid={testId}
    >
      <AlertTriangle className="h-5 w-5 text-red-600 shrink-0 animate-pulse" />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-red-900">{title}</p>
        <p className="text-xs text-red-700 mt-0.5">{subtitle}</p>
      </div>
      <button
        onClick={onToggle}
        className="inline-flex items-center gap-1.5 text-xs font-semibold text-white bg-red-600 hover:bg-red-700 px-3 py-1.5 rounded-md transition"
        data-testid={toggleTestId}
      >
        {open ? "Hide" : "Show"} & Assign
        {open ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
      </button>
      {open && (
        <div className="absolute left-0 right-0 top-full mt-2 bg-white border border-red-200 rounded-lg shadow-xl z-20 max-h-[60vh] overflow-y-auto">
          <div className="divide-y divide-slate-100">{children}</div>
        </div>
      )}
    </div>
  );
}

function BannerRow({ project, missingLabel, testId, actionLabel, actionTestId, onAssign }) {
  return (
    <div className="flex items-center gap-3 px-4 py-3 hover:bg-red-50/40" data-testid={testId}>
      <FolderOpen className="h-4 w-4 text-red-500 shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="font-medium text-slate-800 truncate">{project.name}</div>
        <div className="text-[11px] text-red-600">Missing: {missingLabel}</div>
      </div>
      <Button
        size="sm"
        onClick={onAssign}
        className="bg-red-600 hover:bg-red-700 text-white h-7 text-xs"
        data-testid={actionTestId}
      >
        {actionLabel}
      </Button>
      <Link to={`/projects/${project.id}`} className="text-xs text-slate-500 hover:text-purple-600 flex items-center gap-1">
        Open <ExternalLink className="h-3 w-3" />
      </Link>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  AI-ready banner — soft purple gradient. Only visible when there is at    */
/*  least one project ready AND the current user can push to AI.             */
/* -------------------------------------------------------------------------- */
export function AiReadyBanner({
  aiReadyList,
  aiReadyTraffic,
  aiSelectedIds,
  aiAllSelected,
  toggleAiAll,
  toggleAiOne,
  onPush,
  onIgnore,
}) {
  return (
    <div
      className="relative overflow-hidden rounded-xl border border-purple-200 bg-gradient-to-br from-purple-50 via-fuchsia-50 to-indigo-50 p-5"
      data-testid="ai-ready-banner"
    >
      <div className="absolute -right-6 -top-6 h-32 w-32 rounded-full bg-purple-200/40 blur-3xl" />
      <div className="relative">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0">
            <div className="flex items-center gap-2 text-purple-700 font-semibold">
              <Sparkles className="h-5 w-5" />
              These projects are ready for AI team
            </div>
            <div className="text-sm text-slate-600 mt-1">
              {aiReadyList.length} project{aiReadyList.length === 1 ? "" : "s"} with traffic ≥ {aiReadyTraffic.toLocaleString()}. Pick the ones to hand off.
            </div>
          </div>
          <Button
            onClick={onPush}
            disabled={aiSelectedIds.length === 0}
            className="bg-purple-600 hover:bg-purple-700 text-white shrink-0 disabled:opacity-50"
            data-testid="push-to-ai-btn"
          >
            Push {aiSelectedIds.length > 0 ? `${aiSelectedIds.length} ` : ""}to AI <ArrowRight className="h-4 w-4 ml-1.5" />
          </Button>
        </div>

        <div className="mt-3 rounded-lg bg-white/70 border border-purple-100 divide-y divide-purple-100">
          <label className="flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-white/80 rounded-t-lg" data-testid="ai-select-all-label">
            <input
              type="checkbox"
              checked={aiAllSelected}
              onChange={toggleAiAll}
              className="h-4 w-4 rounded border-slate-300 text-purple-600 focus:ring-purple-500"
              data-testid="ai-select-all"
            />
            <span className="text-xs font-semibold text-slate-700 uppercase tracking-wider">
              {aiAllSelected ? "Deselect all" : "Select all"}
            </span>
            <span className="ml-auto text-[11px] text-slate-500">
              {aiSelectedIds.length} / {aiReadyList.length} selected
            </span>
          </label>
          {aiReadyList.map((p) => {
            const isSel = aiSelectedIds.includes(p.id);
            const traffic = Number((p.seo_performance || {}).traffic || 0);
            return (
              <div
                key={p.id}
                className={`flex items-center gap-2 px-3 py-2 transition ${isSel ? "bg-purple-50/50" : "hover:bg-white"}`}
                data-testid={`ai-ready-row-${p.id}`}
              >
                <label className="flex items-center gap-2 flex-1 min-w-0 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={isSel}
                    onChange={() => toggleAiOne(p.id)}
                    className="h-4 w-4 rounded border-slate-300 text-purple-600 focus:ring-purple-500"
                    data-testid={`ai-ready-check-${p.id}`}
                  />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-slate-800 truncate">{p.name}</div>
                    {p.site_url && <div className="text-[11px] text-slate-400 truncate">{p.site_url}</div>}
                  </div>
                  <span className="text-[11px] px-2 py-0.5 rounded-full bg-white/80 border border-purple-200 text-purple-800 font-medium whitespace-nowrap">
                    {traffic.toLocaleString()} traffic
                  </span>
                </label>
                {/* Iter 42.3 — per-project Ignore. Hides the row from the
                    banner (persisted server-side via POST /ai-ignore) so
                    the CM can dismiss projects they don't want handed off
                    to the AI team without losing them from the main table. */}
                {onIgnore && (
                  <button
                    type="button"
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); onIgnore(p); }}
                    className="text-[11px] px-2 py-1 rounded-md text-slate-500 hover:text-white hover:bg-slate-600 border border-slate-200 hover:border-slate-600 transition flex items-center gap-1 whitespace-nowrap"
                    title="Ignore — hide from AI-ready banner"
                    data-testid={`ai-ignore-btn-${p.id}`}
                  >
                    <EyeOff className="h-3 w-3" /> Ignore
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Unassigned banner — CMs / TLs (and everyone) get nudged about projects   */
/*  missing either a TL or a CM.                                              */
/* -------------------------------------------------------------------------- */
export function UnassignedBanner({ unassignedList, open, setOpen, onAssign }) {
  return (
    <ShakeBanner
      open={open}
      onToggle={() => setOpen((v) => !v)}
      title={`You have ${unassignedList.length} project${unassignedList.length === 1 ? "" : "s"} unassigned`}
      subtitle="Each unassigned project is missing a Team Lead or Content Manager."
      testId="unassigned-alert"
      toggleTestId="unassigned-dropdown-toggle"
    >
      {unassignedList.map((p) => {
        const missing = [];
        if (!(p.team_lead_ids && p.team_lead_ids.length > 0)) missing.push("Team Lead");
        if (!(p.content_manager_ids && p.content_manager_ids.length > 0)) missing.push("Content Manager");
        return (
          <BannerRow
            key={p.id}
            project={p}
            missingLabel={missing.join(" & ")}
            testId={`unassigned-row-${p.id}`}
            actionLabel="Assign"
            actionTestId={`unassigned-assign-${p.id}`}
            onAssign={() => { onAssign(p); setOpen(false); }}
          />
        );
      })}
    </ShakeBanner>
  );
}

/* -------------------------------------------------------------------------- */
/*  Content-TL–only banner: projects assigned to me that have no writers.     */
/* -------------------------------------------------------------------------- */
export function MissingWriterBanner({ missingWriterList, open, setOpen, onAssign }) {
  return (
    <ShakeBanner
      open={open}
      onToggle={() => setOpen((v) => !v)}
      title={`${missingWriterList.length} of your project${missingWriterList.length === 1 ? " has" : "s have"} no writer assigned`}
      subtitle="Assign a writer so work can begin."
      testId="missing-writer-alert"
      toggleTestId="missing-writer-dropdown-toggle"
    >
      {missingWriterList.map((p) => (
        <BannerRow
          key={p.id}
          project={p}
          missingLabel="Writer"
          testId={`missing-writer-row-${p.id}`}
          actionLabel="Assign Writer"
          actionTestId={`missing-writer-assign-${p.id}`}
          onAssign={() => { onAssign(p); setOpen(false); }}
        />
      ))}
    </ShakeBanner>
  );
}

/* -------------------------------------------------------------------------- */
/*  Publishing-TL–only banner: projects assigned to me that have no          */
/*  publishers.                                                               */
/* -------------------------------------------------------------------------- */
export function MissingPublisherBanner({ missingPublisherList, open, setOpen, onAssign }) {
  return (
    <ShakeBanner
      open={open}
      onToggle={() => setOpen((v) => !v)}
      title={`${missingPublisherList.length} of your project${missingPublisherList.length === 1 ? " has" : "s have"} no publisher assigned`}
      subtitle="Assign a publisher so content can be scheduled."
      testId="missing-publisher-alert"
      toggleTestId="missing-publisher-dropdown-toggle"
    >
      {missingPublisherList.map((p) => (
        <BannerRow
          key={p.id}
          project={p}
          missingLabel="Publisher"
          testId={`missing-publisher-row-${p.id}`}
          actionLabel="Assign Publisher"
          actionTestId={`missing-publisher-assign-${p.id}`}
          onAssign={() => { onAssign(p); setOpen(false); }}
        />
      ))}
    </ShakeBanner>
  );
}

/* -------------------------------------------------------------------------- */
/*  KW-unassigned banner — non-shaking (always visible), lists Content        */
/*  projects lacking keywords. Rows expose Request/Revoke buttons.            */
/* -------------------------------------------------------------------------- */
export function KwUnassignedBanner({ kwUnassignedList, onRequest, onRevoke }) {
  return (
    <div className="shake-animation rounded-lg border border-red-300 bg-red-50 px-4 py-3 space-y-2" data-testid="kw-unassigned-banner">
      <div className="flex items-center gap-2">
        <AlertTriangle className="h-5 w-5 text-red-600 animate-pulse" />
        <div className="flex-1">
          <div className="text-sm font-semibold text-red-900">{kwUnassignedList.length} project{kwUnassignedList.length === 1 ? "" : "s"} without keywords</div>
          <div className="text-xs text-red-700 mt-0.5">Request keywords from the Keyword Planning team. You can revoke a request anytime.</div>
        </div>
      </div>
      <ul className="divide-y divide-red-200 border-t border-red-200 max-h-[240px] overflow-y-auto">
        {kwUnassignedList.map((p) => (
          <li key={p.id} className="py-2 flex items-center gap-3" data-testid={`kw-un-row-${p.id}`}>
            <FolderOpen className="h-4 w-4 text-red-500 shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-slate-800 truncate">{p.name}</div>
              <div className="text-[11px] text-red-600">
                {p.kw_requested ? "KW Requested" : "No keywords yet"}
                {p.site_url ? <> · <span className="text-red-500">{p.site_url}</span></> : null}
              </div>
            </div>
            {p.kw_requested ? (
              <>
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-200 font-semibold">KW Requested</span>
                <button onClick={() => onRevoke(p)} className="text-[11px] font-semibold text-slate-600 hover:text-white hover:bg-slate-600 border border-slate-300 px-2 py-1 rounded" data-testid={`revoke-kw-${p.id}`}>
                  Revoke
                </button>
              </>
            ) : (
              <button onClick={() => onRequest(p)} className="text-xs bg-red-600 hover:bg-red-700 text-white px-2 py-1 rounded font-semibold" data-testid={`request-kw-banner-${p.id}`}>
                Request Keyword
              </button>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
